<template>
  <div class="video-detail-page">
    <van-nav-bar fixed placeholder class="page-nav" :border="false">
      <template #left>
        <img
          class="back-icon"
          src="https://m.sincereloves.online/icon/download.png"
          alt="back"
          @click="back"
        >
      </template>
      <template #title>
        <span class="page-title">{{ $t('ui.video.detail_title') }}</span>
      </template>
      <template #right>
        <van-icon name="home-o" class="home-icon" @click="$router.push('/Home')" />
      </template>
    </van-nav-bar>

    <div class="page-content">
      <section class="player-section">
        <video
          v-if="useNativePlayer"
          ref="nativeVideo"
          class="native-video"
          :src="nowPlayVideoUrl"
          :poster="cover"
          controls
          playsinline
          webkit-playsinline="true"
          preload="metadata"
          @play="handleVideoPlay"
          @error="handleVideoError"
        ></video>
        <video
          v-else
          id="my-video"
          ref="videoJsPlayer"
          class="video-js vjs-default-skin"
        ></video>
      </section>

      <section class="title-section">
        <div class="title-card">
          {{ displayTitle }}
        </div>
      </section>

      <section class="limit-section">
        <button class="limit-card" type="button" @click="openSupport">
          <div class="limit-text">
            <strong>{{ $t('ui.video.remaining_times', { n: remainingTimes }) }}</strong>
            <span>{{ $t('ui.video.upgrade_vip') }}</span>
          </div>
          <div class="limit-arrow-wrap">
            <van-icon name="arrow" class="limit-arrow" />
          </div>
        </button>
      </section>

      <section class="like-section">
        <div class="section-head">
          <span class="section-line"></span>
          <span class="section-title">{{ $t('ui.video.also_like') }}</span>
        </div>
      </section>

      <section v-if="normalizedRecommendations.length" class="recommend-section">
        <div class="recommend-grid">
          <article
            v-for="item in normalizedRecommendations"
            :key="item.id"
            class="recommend-card"
            @click="toPlayVideo(item.id)"
          >
            <div class="recommend-cover-wrap">
              <img :src="item.vod_pic" :alt="item.vod_name" class="recommend-cover">
              <div class="recommend-count">{{ $t('ui.video.plays', { n: item.count }) }}</div>
            </div>
            <div class="recommend-name">{{ item.vod_name }}</div>
          </article>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import videojs from 'video.js';
import 'videojs-contrib-hls';
import 'video.js/dist/video-js.css';
import { getVideoBanMessage } from '../../utils/videoAccess';

export default {
  data() {
    return {
      nowPlayVideoUrl: '',
      cover: '',
      userInfo: [],
      videoInfo: {},
      moreVideoInfo: [],
      player: null,
      is_play: false,
      times: null,
      useNativePlayer: false,
      scrollbarStyleId: 'play-video-scrollbar-style'
    };
  },
  computed: {
    displayTitle() {
      return this.videoInfo.vod_name || this.$t('ui.brand.name');
    },
    remainingTimes() {
      const watchNum = Number(this.userInfo.watch_num);
      if (Number.isFinite(watchNum)) {
        return Math.max(0, Math.trunc(watchNum));
      }
      return 0;
    },
    hasWatchQuota() {
      return this.remainingTimes > 0;
    },
    normalizedRecommendations() {
      return Array.isArray(this.moreVideoInfo)
        ? this.moreVideoInfo.map((item) => ({
          ...item,
          vod_pic: this.normalizeAssetUrl(item.vod_pic || item.cover),
          count: Number(item.count || 0) || 999
        }))
        : [];
    }
  },
  methods: {
    normalizeAssetUrl(url) {
      if (!url || typeof url !== 'string') {
        return '';
      }

      const origin = 'https://admin699.ltalk6.com';
      const normalizedUrl = url.trim();

      if (/^https?:\/\/127\.0\.0\.1\//.test(normalizedUrl)) {
        return normalizedUrl.replace(/^https?:\/\/127\.0\.0\.1\//, `${origin}/`);
      }

      if (/^https?:\/\/localhost:8081\//.test(normalizedUrl)) {
        return normalizedUrl.replace(/^https?:\/\/localhost:8081\//, `${origin}/`);
      }

      if (/^https?:\/\/[^/]*gxcg01\.buzz\//i.test(normalizedUrl)) {
        return normalizedUrl.replace(/^https?:\/\/[^/]*gxcg01\.buzz\//i, `${origin}/`);
      }

      if (normalizedUrl.startsWith('/')) {
        return `${origin}${normalizedUrl}`;
      }

      if (!/^https?:\/\//i.test(normalizedUrl)) {
        return `${origin}/${normalizedUrl.replace(/^\.?\//, '')}`;
      }

      return normalizedUrl;
    },
    getVideoSourceType(url) {
      const cleanUrl = String(url || '').split('?')[0].split('#')[0].toLowerCase();

      if (cleanUrl.endsWith('.m3u8')) {
        return 'application/x-mpegURL';
      }
      if (cleanUrl.endsWith('.mp4') || cleanUrl.endsWith('.m4v')) {
        return 'video/mp4';
      }
      if (cleanUrl.endsWith('.webm')) {
        return 'video/webm';
      }
      if (cleanUrl.endsWith('.ogg') || cleanUrl.endsWith('.ogv')) {
        return 'video/ogg';
      }
      if (cleanUrl.endsWith('.mov')) {
        return 'video/quicktime';
      }

      return 'video/mp4';
    },
    shouldUseNativePlayer(url) {
      return this.getVideoSourceType(url) !== 'application/x-mpegURL';
    },
    back() {
      this.$router.push({ path: '/Video' });
    },
    openSupport() {
      const service = this.$store.getters.getBaseInfo || {};
      if (service.iskefu == 1 && service.kefu) {
        window.location.href = service.kefu;
        return;
      }
      this.$router.push('/ServiceOnline');
    },
    getVideoInfo() {
      this.$http({
        method: 'get',
        data: { id: this.$route.query.id },
        url: 'video_get_info'
      }).then((res) => {
        if (res && res.code === 403) {
          this.$toast(res.msg || this.$t('ui.common.contact_cs'));
          this.$router.replace({ path: '/Video' });
          return;
        }

        const info = res && res.data && typeof res.data === 'object' ? res.data : {};
        info.vod_pic = this.normalizeAssetUrl(info.vod_pic);
        this.videoInfo = info;
        this.nowPlayVideoUrl = this.normalizeAssetUrl(info.vod_play_url || '');
        this.cover = info.vod_pic || '';
        this.getVideo();
      });
    },
    toPlayVideo(id) {
      if (!localStorage.getItem('token')) {
        this.$router.push({ path: '/Login' });
        return;
      }

      const message = getVideoBanMessage(this.userInfo, this.$t.bind(this));
      if (message) {
        this.$toast(message);
        return;
      }

      this.$router.push({ path: '/PlayVideo', query: { id } });
      window.location.reload();
    },
    getMoreVideoItem() {
      this.$http({
        method: 'get',
        data: {
          id: this.$route.query.id
        },
        url: 'video_get_more_item'
      }).then((res) => {
        this.moreVideoInfo = Array.isArray(res.data) ? res.data : [];
      });
    },
    getVideo() {
      if (!this.nowPlayVideoUrl) {
        return;
      }

      if (this.shouldUseNativePlayer(this.nowPlayVideoUrl)) {
        this.disposePlayer();
        this.useNativePlayer = true;
        this.$nextTick(() => {
          const video = this.$refs.nativeVideo;
          if (video) {
            video.pause();
            video.src = this.nowPlayVideoUrl;
            video.poster = this.cover;
            video.load();
          }
        });
        return;
      }

      this.useNativePlayer = false;
      this.$nextTick(() => {
        const player = this.ensurePlayer();
        if (!player) {
          return;
        }

        player.src([
          {
            src: this.nowPlayVideoUrl,
            type: this.getVideoSourceType(this.nowPlayVideoUrl)
          }
        ]);
        player.poster(this.cover);
        player.load();
      });
    },
    ensurePlayer() {
      if (this.player) {
        return this.player;
      }

      const playerEl = this.$refs.videoJsPlayer;
      if (!playerEl) {
        return null;
      }

      this.player = videojs(
        playerEl,
        {
          height: '240px',
          preload: 'auto',
          controls: true,
          fluid: true,
          responsive: true,
          playbackRates: [0.75, 1, 1.5, 2]
        },
        () => {
          this.player.on('play', this.handleVideoPlay);
          this.player.on('error', this.handleVideoError);
        }
      );

      return this.player;
    },
    disposePlayer() {
      if (this.player) {
        this.player.dispose();
        this.player = null;
      }
    },
    handleVideoPlay() {
      this.is_play = true;
    },
    handleVideoError() {
      if (!this.useNativePlayer && this.shouldUseNativePlayer(this.nowPlayVideoUrl)) {
        this.disposePlayer();
        this.useNativePlayer = true;
        this.$nextTick(() => {
          const video = this.$refs.nativeVideo;
          if (video) {
            video.src = this.nowPlayVideoUrl;
            video.poster = this.cover;
            video.load();
          }
        });
        return;
      }

      this.$toast(this.$t('ui.video.toast_unsupported'));
    },
    getCurrentPlaybackTime() {
      if (this.useNativePlayer) {
        const video = this.$refs.nativeVideo;
        return video ? Number(video.currentTime || 0) : 0;
      }

      return this.player ? Number(this.player.currentTime() || 0) : 0;
    },
    pausePlayback() {
      if (this.useNativePlayer) {
        const video = this.$refs.nativeVideo;
        if (video) {
          video.pause();
        }
        return;
      }

      if (this.player) {
        this.player.pause();
      }
    },
    hideRootScrollbar() {
      if (typeof document === 'undefined') {
        return;
      }

      const root = document.documentElement;
      const body = document.body;

      root.style.setProperty('scrollbar-width', 'none');
      body.style.setProperty('scrollbar-width', 'none');
      root.style.setProperty('-ms-overflow-style', 'none');
      body.style.setProperty('-ms-overflow-style', 'none');

      if (!document.getElementById(this.scrollbarStyleId)) {
        const style = document.createElement('style');
        style.id = this.scrollbarStyleId;
        style.textContent = `
html::-webkit-scrollbar,
body::-webkit-scrollbar {
  width: 0 !important;
  height: 0 !important;
  display: none !important;
}
        `;
        document.head.appendChild(style);
      }
    },
    restoreRootScrollbar() {
      if (typeof document === 'undefined') {
        return;
      }

      const root = document.documentElement;
      const body = document.body;
      const style = document.getElementById(this.scrollbarStyleId);

      root.style.removeProperty('scrollbar-width');
      body.style.removeProperty('scrollbar-width');
      root.style.removeProperty('-ms-overflow-style');
      body.style.removeProperty('-ms-overflow-style');

      if (style && style.parentNode) {
        style.parentNode.removeChild(style);
      }
    },
    getUserInfo() {
      this.$http({
        method: 'get',
        url: 'user_info'
      }).then((res) => {
        if (res.code === 200) {
          this.userInfo = res.data;
          const banMessage = getVideoBanMessage(this.userInfo, this.$t.bind(this));
          if (banMessage) {
            this.$toast(banMessage);
            this.$router.replace({ path: '/Video' });
            return;
          }
          if (this.userInfo.status !== 1) {
            this.$toast(this.$t('video.account_out'));
            localStorage.clear();
            this.$router.push({ path: '/Login' });
          } else {
            this.getVideoInfo();
            this.getMoreVideoItem();
          }
        } else if (res.code === 401) {
          this.$toast(res.msg);
        }
      });
    }
  },
  mounted() {
    if (!localStorage.getItem('token')) {
      this.$router.push({ path: '/Login' });
      return;
    }

    this.hideRootScrollbar();
    this.getUserInfo();

    this.times = setInterval(() => {
      if (this.is_play && !this.hasWatchQuota) {
        const ct = Math.round(this.getCurrentPlaybackTime());
        if (ct >= 180) {
          this.pausePlayback();
          this.$toast(this.$t('video.buy'));
        }
      }
    }, 2000);
  },
  beforeDestroy() {
    this.disposePlayer();
    if (this.is_play) {
      this.is_play = false;
    }
    clearInterval(this.times);
    this.restoreRootScrollbar();
  }
};
</script>

<style scoped>
.video-detail-page {
  min-height: 100vh;
  background: #020d24;
  color: #fff;
}

.page-nav {
  min-height: 98px;
  background: #1c1c1e;
}

::v-deep .page-nav .van-nav-bar {
  background: #1c1c1e;
  box-shadow: none;
}

::v-deep .page-nav .van-nav-bar__content {
  height: 88px;
}

::v-deep .page-nav .van-nav-bar__left,
::v-deep .page-nav .van-nav-bar__right {
  padding: 0 18px;
}

.back-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.page-title {
  color: #e6b98a;
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
}

.home-icon {
  color: #e6b98a;
  font-size: 38px;
}

.page-content {
  padding: 8px 10px 110px;
}

.player-section,
.title-card,
.section-head,
.recommend-card {
  border: 1px solid #263a5c;
  background: linear-gradient(90deg, #1f222a, #2c2f37);
}

.player-section {
  overflow: hidden;
  border-radius: 14px;
  background: #101010;
}

.video-js,
.native-video {
  width: 100%;
  min-height: 240px;
  font-size: 16px;
  display: block;
  background: #000;
  object-fit: contain;
}

::v-deep .video-js .vjs-big-play-button {
  top: 50%;
  left: 50%;
  margin-top: -28px;
  margin-left: -46px;
}

.title-section,
.limit-section,
.like-section,
.recommend-section {
  margin-top: 16px;
}

.title-card {
  padding: 22px 18px;
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.45;
}

.limit-card {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  padding: 18px 18px 18px 28px;
  border: none;
  border-radius: 999px;
  background: linear-gradient(90deg, #ebbf8b, #d7a468);
  text-align: left;
}

.limit-text {
  display: flex;
  flex: 1;
  flex-direction: column;
  padding-right: 14px;
}

.limit-text strong {
  color: #9c5e18;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.2;
}

.limit-text span {
  margin-top: 4px;
  color: #9c5e18;
  font-size: 14px;
  line-height: 1.2;
}

.limit-arrow-wrap {
  display: flex;
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.82);
}

.limit-arrow {
  color: #c18a4b;
  font-size: 22px;
}

.section-head {
  display: flex;
  align-items: center;
  padding: 14px 12px;
}

.section-line {
  width: 4px;
  height: 24px;
  margin-right: 10px;
  background: #efc79b;
}

.section-title {
  color: #fff;
  font-size: 18px;
  font-weight: 700;
}

.recommend-section {
  margin-top: 10px;
}

.recommend-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.recommend-card {
  overflow: hidden;
  border-radius: 14px;
}

.recommend-cover-wrap {
  position: relative;
}

.recommend-cover {
  display: block;
  width: 100%;
  height: 160px;
  object-fit: cover;
}

.recommend-count {
  position: absolute;
  right: 8px;
  bottom: 8px;
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 11px;
}

.recommend-name {
  padding: 10px 10px 12px;
  overflow: hidden;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
</style>
